import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { SIZES, ALIGN, HTML_TAGS } from "../../common/constants/commonConstants.js";
import { getProcesses } from "../../services/campaign.js";
import { PROCESSES_COLUMNS, PROCESSES_ERRORS, STATUS_COLORS } from "../../common/constants/campaignModule/processesConstant.js";
import { useClientTimeZone, formatDateInZone, zoneAbbr } from "../../utils/clienttimezone";
import ProgressBar from "../../common/components/ProgressBar.jsx";
import RefreshCountdonwn from "./refreshCountdonwn.jsx";

// ─────────────────────────────────────────────────────────────────────────────
// PROCESSES_COLUMNS in processesConstant.js must match the cell order below.
// "Failed" and "InitiatedBy" are gone; "Reference" sits after Type:
//
//   { id: "Type",      label: "Type" },
//   { id: "Reference", label: "Reference" },
//   { id: "Status",    label: "Status" },
//   { id: "Progress",  label: "Progress" },
//   { id: "Total",     label: "Total" },
//   { id: "Start",     label: "Start" },
//   { id: "End",       label: "End" },
//
// colSpan uses PROCESSES_COLUMNS.length, so the empty/loading rows follow
// automatically.
// ─────────────────────────────────────────────────────────────────────────────

const getStatusChip = (status) => {
    const style = STATUS_COLORS[status] ?? { bg: "#eeeeee", color: "#424242" };
    return (
        <Chip
            label={status ?? "-"}
            size="small"
            sx={{ backgroundColor: style.bg, color: style.color, fontWeight: 600, fontSize: "0.75rem" }}
        />
    );
};

/**
 * What the job is actually about — the campaign, report or schedule name,
 * resolved server-side from ProcessType + CampaignID.
 *
 * ReferenceName (the raw blob name, or "ScheduleID=196") goes in the tooltip:
 * it's what you need when tracing a specific run, but it's noise in the
 * column itself.
 */
const getReferenceCell = (process) => {
    const name = process.Reference;
    const raw = process.ReferenceName;

    if (!name) {
        return <Typography component="span" sx={{ color: "text.disabled" }}>-</Typography>;
    }

    const cell = (
        <Typography
            component="span"
            sx={{
                fontSize: "0.8rem",
                display: "inline-block",
                maxWidth: 260,
                overflow: "hidden",
                textOverflow: "ellipsis",
                verticalAlign: "bottom",
            }}
        >
            {name}
        </Typography>
    );

    return raw ? <Tooltip title={raw} arrow>{cell}</Tooltip> : cell;
};

// Column ids whose header gets the zone appended.
const TIME_COLUMN_IDS = new Set(["Start", "End"]);

const REFRESH_INTERVAL = 5;

function Processes() {
    const [processes, setProcesses]         = useState([]);
    const [isLoading, setIsLoading]         = useState(false);
    const [page, setPage]                   = useState(0);
    const [rowsPerPage, setRowsPerPage]     = useState(10);
    const [resetSignal, setResetSignal]     = useState(0);

    // Start/End render in the configured client zone. The old local
    // formatDateTime used toLocaleString(), which put a job that ran at
    // 2:00 AM PT at 5:00 AM for anyone watching from the east coast.
    const { timeZone } = useClientTimeZone();

    useEffect(() => {
        const fetchProcesses = async (showLoader = false) => {
            if (showLoader) setIsLoading(true);
            try {
                const data = await getProcesses();
                setProcesses(data || []);
            } catch (error) {
                console.error(PROCESSES_ERRORS.FETCH_PROCESS_ERROR, error);
            } finally {
                if (showLoader) setIsLoading(false);
            }
        };

        fetchProcesses(true); // initial load — show spinner

        const intervalId = setInterval(() => {
            fetchProcesses(false);
            setResetSignal(prev => prev + 1); // tell RefreshCountdonwn to reset
        }, REFRESH_INTERVAL * 1000);

        return () => clearInterval(intervalId);
    }, []);

    const paginatedProcesses = processes.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const formatDateTime = (value) =>
        value ? formatDateInZone(value, timeZone) : "-";

    const tzLabel = zoneAbbr(timeZone);

    return (
        <>
            <RefreshCountdonwn refreshInterval={REFRESH_INTERVAL} resetSignal={resetSignal} />
            <TableContainer sx={{ mt: 0, overflowX: 'auto', '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }}>

                <Table size={SIZES.SMALL}>
                    <TableHead>
                        <TableRow>
                            {PROCESSES_COLUMNS.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.id === "ID" ? ALIGN.CENTER : ALIGN.LEFT}
                                >
                                    {TIME_COLUMN_IDS.has(column.id)
                                        ? `${column.label} (${tzLabel})`
                                        : column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={PROCESSES_COLUMNS.length} align={ALIGN.CENTER}>
                                    <LinearProgress />
                                </TableCell>
                            </TableRow>
                        ) : paginatedProcesses.length > 0 ? (
                            paginatedProcesses.map((process, index) => (
                                <TableRow hover tabIndex={-1} key={process.ID ?? index}>
                                    <TableCell>{process.Type ?? "-"}</TableCell>
                                    <TableCell>{getReferenceCell(process)}</TableCell>
                                    <TableCell>{getStatusChip(process.Status)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 120 }}>
                                        <ProgressBar progressStr={process.Progress}></ProgressBar>
                                    </TableCell>
                                    <TableCell>{process.Total ?? "-"}</TableCell>
                                    <TableCell>{formatDateTime(process.Start)}</TableCell>
                                    <TableCell>{formatDateTime(process.End)}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={PROCESSES_COLUMNS.length} align={ALIGN.CENTER}>
                                    No processes found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    component={HTML_TAGS.DIV}
                    count={processes.length}
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setPage(0);
                        setRowsPerPage(parseInt(event.target.value, 10));
                    }}
                />
            </TableContainer>
        </>
    );
}

export default Processes;